import React, { useEffect, useState } from "react";
import { getSettings, putSettings, removeDemoUsers } from "../services/api";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";
import { Settings as Cog, Gem, Cake, PartyPopper, Sparkles, Trash2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [s, setS] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  useEffect(() => { getSettings().then(setS).catch(() => {}); }, []);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    try {
      const updated = await putSettings({
        monthlyGems: Number(s.monthlyGems), birthdayGems: Number(s.birthdayGems), anniversaryGems: Number(s.anniversaryGems),
        welcomeGems: Number(s.welcomeGems),
        autoBirthday: s.autoBirthday, autoAnniversary: s.autoAnniversary, autoMonthly: s.autoMonthly,
      });
      setS(updated);
      toast.success("Settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings.");
    }
  };

  const doRemoveDemo = async () => {
    setRemoving(true);
    try {
      const res = await removeDemoUsers();
      toast.success(res.removed > 0 ? `Removed ${res.removed} demo account(s).` : "No demo accounts found.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove demo accounts.");
    } finally {
      setRemoving(false);
      setConfirmRemove(false);
    }
  };

  const Row = ({ icon: Icon, title, desc, children }) => (
    <div className="flex flex-wrap items-center justify-between gap-4 py-5 border-b border-white/5 last:border-0">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-2xl bg-vault-panel2 grid place-items-center"><Icon className="h-5 w-5 text-vault-accent" /></div>
        <div><div className="text-sm font-semibold text-vault-cream">{title}</div><div className="text-xs text-vault-subtle mt-0.5">{desc}</div></div>
      </div>
      <div>{children}</div>
    </div>
  );
  const numCls = "bg-vault-panel2 border-white/10 text-vault-cream w-24 h-10";

  if (!s) return <div className="text-vault-panel">Loading settings\u2026</div>;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-vault-panel/50">Director</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-vault-panel mt-1">System Settings</h1>
      </div>

      <div className="rounded-3xl bg-vault-panel p-6 sm:p-8">
        <h3 className="font-display text-lg font-bold text-vault-cream mb-2 flex items-center gap-2"><Cog className="h-5 w-5 text-vault-accent" /> Gems Configuration</h3>
        <Row icon={Gem} title="Monthly Fixed Gems" desc="Issued to all active employees each month">
          <Input type="number" value={s.monthlyGems} onChange={(e) => set("monthlyGems", e.target.value)} className={numCls} />
        </Row>
        <Row icon={Cake} title="Birthday Gems" desc="Auto-issued on each user's birthday">
          <Input type="number" value={s.birthdayGems} onChange={(e) => set("birthdayGems", e.target.value)} className={numCls} />
        </Row>
        <Row icon={PartyPopper} title="Anniversary Gems" desc="Auto-issued on work-anniversary">
          <Input type="number" value={s.anniversaryGems} onChange={(e) => set("anniversaryGems", e.target.value)} className={numCls} />
        </Row>
        <Row icon={Sparkles} title="Welcome Bonus Gems" desc="Granted once when a new account is created">
          <Input type="number" value={s.welcomeGems} onChange={(e) => set("welcomeGems", e.target.value)} className={numCls} />
        </Row>
      </div>

      <div className="rounded-3xl bg-vault-panel p-6 sm:p-8">
        <h3 className="font-display text-lg font-bold text-vault-cream mb-2">Automation</h3>
        <Row icon={Cake} title="Auto Birthday Gems" desc="Automatically grant birthday gems"><Switch checked={s.autoBirthday} onCheckedChange={(v) => set("autoBirthday", v)} /></Row>
        <Row icon={PartyPopper} title="Auto Anniversary Gems" desc="Automatically grant anniversary gems"><Switch checked={s.autoAnniversary} onCheckedChange={(v) => set("autoAnniversary", v)} /></Row>
        <Row icon={Gem} title="Auto Monthly Gems" desc="Automatically grant monthly gems"><Switch checked={s.autoMonthly} onCheckedChange={(v) => set("autoMonthly", v)} /></Row>
      </div>

      <button onClick={save} className="rounded-2xl bg-vault-accent text-vault-panel font-bold px-6 py-3 hover:bg-vault-accentdark transition-colors">Save Settings</button>

      {/* Danger zone — remove seeded demo accounts (use on production) */}
      <div className="rounded-3xl bg-vault-panel p-6 sm:p-8 border border-red-500/20">
        <h3 className="font-display text-lg font-bold text-vault-cream mb-2 flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-red-400" /> Demo / Test Accounts</h3>
        <p className="text-sm text-vault-subtle mb-5 max-w-xl">
          Permanently remove the built-in demo/test accounts (employee@, manager@, hr@, director@clarkacademy.com and sample staff) and their data from this environment.
          Use this once on your live site so only real accounts remain. Your own account and any real users you've created are never affected.
        </p>
        <button onClick={() => setConfirmRemove(true)}
          className="inline-flex items-center gap-2 rounded-2xl bg-red-900/40 text-red-300 font-semibold px-6 py-3 hover:bg-red-900/60 transition-colors">
          <Trash2 className="h-4 w-4" /> Remove demo accounts
        </button>
      </div>

      <AlertDialog open={confirmRemove} onOpenChange={setConfirmRemove}>
        <AlertDialogContent className="bg-vault-panel border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-vault-cream font-display">Remove all demo accounts?</AlertDialogTitle>
            <AlertDialogDescription className="text-vault-subtle">
              This permanently deletes the seeded demo/test users and their requests, gems, and notifications. Real accounts you've created (including your own) are kept. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-vault-panel3 text-vault-cream border-0 hover:bg-vault-panel2">Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={removing} onClick={doRemoveDemo} className="bg-red-700 text-white hover:bg-red-600">
              {removing ? "Removing…" : "Remove demo accounts"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
